/* eslint-disable @typescript-eslint/naming-convention */
/* http-headers */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FilterOptions } from '@app/shared/models/meter-data/meter-operator-filter';
import { MeterWriting } from '@app/shared/models/meter-data/meter-reading';
import { MeterView, MeterViewResponse } from '@app/shared/models/meter-data/meter-view';
import { FilterOptionTypes, MeterReadingType, MsbCategories } from '@app/shared/enums';
import { TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MeterFilterService } from '../meter-filter.service';
import { ApiService } from './api.service';
import { MunicipalityMeterNumber } from '@app/zahlerstand/models/municipality-meter-number';

@Injectable({
  providedIn: 'root',
})
export class MeterDataService extends ApiService {
  private _meterFilterOptions$ = new BehaviorSubject<FilterOptions[]>([]);

  public get meterFilterOptions$(): Observable<FilterOptions[]> {
    return this._meterFilterOptions$.asObservable();
  }

  public get meterFilterOptions(): FilterOptions[] {
    return this._meterFilterOptions$.value;
  }

  constructor(
    public http: HttpClient,
    public translate: TranslateService,
    private cookieService: CookieService,
    private meterFilterService: MeterFilterService
  ) {
    super(http);
  }

  dbColumnNames = [
    'Guid',
    'EnergyTypeId',
    'MeterTypeId',
    'MeterTypeInfo',
    'ConnectionObjectNumber',
    'BuildingId',
    'SerialNumber',
    'MeterNumber',
    'RegisterNumber',
    'MunicipalityId',
    'MunicipalityKey',
    'Municipality',
    'District',
    'Street',
    'HouseNumber',
    'HouseNumberAdditive',
    'LocationInfo',
    'ZipCode',
    'Latitude',
    'Longitude',
    'KomKey',
    'OperatingRange',
    'IsIMSysInstalled',
    'Name',
    'Name2',
    'Name3',
    'Description',
    'Code',
    'InstallationNumber',
    'InstallationType',
    'Profile1Id',
    'Profile1External',
    'Profile1Type',
    'ContractNumber',
    'BusinessPartnerNumber',
    'Scope',
    'BillingCategory',
    'GridLevelMeloKey',
    'MeasurementLocationKey',
    'MarketLocationKey',
    'Notes',
    'EnergyQuantityBalancing',
    'UsageStartDate',
    'UsageEndDate',
    'DetailedMeterType',
    'IsEnergyGuardLicenseAvailable',
    'ConverterFactor',
    'CreationDate',
    'ModifyDate',
    'ModifyUser',
    'ContractAccount',
    'ObjectType',
    'ReadingGrouping',
    'SensorID',
    'DeviceEUI',
    'IsIOTFeedOnly',
    'objectTypeModifiedBy',
    'objectTypeModifyDate',
    'ProductId',
  ];

  public getViewMeterFilterOptions(municipalityKey: string): Observable<void> {
    const url = this.endpoint(`socrates/meters/api/views/ViewMeterFilterOptions`);
    const query = `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    return this.http.get<FilterOptions[]>(url + `?${query}`).pipe(
      catchError(() => of([])),
      map((response: FilterOptions[]): void => {
        const transformedOptions = response.map(this.transformFilterOptions);
        const filterOptions: FilterOptions[] = Object.values(
          transformedOptions.reduce((accumulator: { [key: string]: FilterOptions }, options: FilterOptions) => {
            const { name, msbid, msbCategory } = options;
            const key = `${name}_${options.lineOfBusiness}`;

            if (!accumulator[key]) {
              accumulator[key] = {
                ...options,
                msbid: msbCategory === MsbCategories.GMSB && msbid !== 'others' ? '[null]' : '',
                msbCategory: this.setMsbCategory(msbCategory, name),
              };
            }

            accumulator[key].msbid = msbid.toString().concat(!msbid ? '' : ';', accumulator[key].msbid);

            return accumulator;
          }, {})
        );

        this._meterFilterOptions$.next(filterOptions);
      })
    );
  }

  public getMeterViewByMunicipality(
    municipalityKey: string,
    searchText: string,
    energyTypesFilter: Array<string>,
    meterTypesFilter: Array<string>,
    marketOperatorsFilter: Array<string>,
    pageNumber: number,
    resultsPerPage: number,
    orderClause: string,
    isForMeterReading: boolean,
    serialNumbers: string[],
    usageEndDateFilter: string | null,
    connectionObjectNumber: string = '',
    getDistinct: boolean = false
  ): Observable<MeterViewResponse> {
    let q = '';

    let url = this.endpoint('socrates/meters/api/views/');
    url += !getDistinct ? 'ViewMetersExtendedInfo1' : isForMeterReading ? 'ViewMetersSLPDistinctInfo1' : 'ViewMetersDistinctInfo1';

    q += `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    if (searchText) {
      const searchTextFilter = this.meterFilterService.setSearchTextFilter(searchText, getDistinct);
      if (searchTextFilter.length > 0) {
        q += ` and ${searchTextFilter}`;
      }
    }

    const komkeyfilter = this.meterFilterService.setKomKeyFilters(isForMeterReading, municipalityKey);
    if (komkeyfilter.length > 0) {
      q += ` and ${komkeyfilter}`;
    }

    const energyTypes = this.meterFilterService.setEnergyTypesFilter(energyTypesFilter, isForMeterReading);
    if (energyTypes.length > 0) {
      q += ` and ${energyTypes}`;
    }

    const meterTypes = this.meterFilterService.setMeterTypesFilter(meterTypesFilter, isForMeterReading);
    if (meterTypes.length > 0) {
      q += ` and ${meterTypes}`;
    }

    const marketOpFilter = this.meterFilterService.setMarketOperatorFilter(marketOperatorsFilter, isForMeterReading, this.meterFilterOptions);
    if (marketOpFilter.length > 0) {
      q += ` and ${marketOpFilter}`;
    }

    const meterNoFilter = this.meterFilterService.setMeterNumbersFilter(serialNumbers);
    if (meterNoFilter.length > 0) {
      q += ` and ${meterNoFilter}`;
    }

    const activeOnlyFilter = this.meterFilterService.setActiveOnlyFilter(usageEndDateFilter);
    if (activeOnlyFilter.length > 0) {
      q += ` and ${activeOnlyFilter}`;
    }

    if (isForMeterReading) {
      const ableseeinheitFilter = this.meterFilterService.setAbleseeinheitFilter();
      if (ableseeinheitFilter.length > 0) {
        q += ` and ${ableseeinheitFilter}`;
      }
    }

    if (!isNil(connectionObjectNumber) && connectionObjectNumber !== '') {
      const connObjFilter = this.meterFilterService.setBuildingFilter([connectionObjectNumber]);
      if (connObjFilter.length > 0) {
        q += ` and ${connObjFilter}`;
      }
    }

    q += resultsPerPage > 0 && pageNumber >= 0 ? `&$top=${resultsPerPage}&$skip=${pageNumber * resultsPerPage}` : '';

    if (orderClause === 'notes asc' || orderClause === 'notes desc') {
      if (!getDistinct) {
        orderClause = `${orderClause}`;
      } else {
        orderClause = `meter${orderClause}`;
      }
    }

    if ((orderClause === 'buildingTypesName asc' || orderClause === 'buildingTypesName desc') && getDistinct) {
      orderClause = `${orderClause}`;
    } else if (orderClause === 'buildingTypesName asc' && !getDistinct) {
      orderClause = 'objectType asc';
    } else if (orderClause === 'buildingTypesName desc' && !getDistinct) {
      orderClause = 'objectType desc';
    }

    q += orderClause.length > 0 ? '&$orderby=' + orderClause : '&$orderby=Street,HouseNumber,ZipCode,District';

    q += `&$count=true`;

    return this.http.get<MeterViewResponse>(url + `?${q}`);
  }

  public getMetersByMelo(measurementLocationKey: string, getOnlyActiveMeters: boolean = true, municipalityKey?: string): Observable<MeterViewResponse> {
    let q = '';

    const url = this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1`);

    q += `$filter=${this.meterFilterService.setMeasurementLocationKey(measurementLocationKey)}`;

    const komkeyfilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey as string);
    if (komkeyfilter.length > 0) {
      q += ` and ${komkeyfilter}`;
    }

    if (getOnlyActiveMeters) {
      const isActiveOnlyFilter = this.meterFilterService.setActiveOnlyFilter();
      if (isActiveOnlyFilter.length > 0) {
        q += ` and ${isActiveOnlyFilter}`;
      }
    }

    q += `&$count=true`;

    return this.http.get<MeterViewResponse>(url + `?${q}`);
  }

  public getHistoricSmartMeter(
    municipalityKey: string,
    connectionNumber: string[],
    meterNumber: string,
    marketLocationKey: string,
    contractAccount: string
  ): Observable<MeterView[]> {
    const url = this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1`);

    let q = '';

    q += `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    const buildingFilter = this.meterFilterService.setBuildingFilter(connectionNumber);
    if (buildingFilter.length > 0) {
      q += ` and ${buildingFilter}`;
    }

    const meterNoFilter = this.meterFilterService.setMeterNumberFilter(meterNumber);
    if (meterNoFilter.length > 0) {
      q += ` and ${meterNoFilter}`;
    }

    const maloFilter = this.meterFilterService.setMarketLocationKeyFilter(marketLocationKey);
    if (maloFilter.length > 0) {
      q += ` and ${maloFilter}`;
    }

    const contractAcctFilter = this.meterFilterService.setContractAccountFilter(contractAccount);
    if (contractAcctFilter.length > 0) {
      q += ` and ${contractAcctFilter}`;
    }

    const komkeyFilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey);
    if (komkeyFilter.length > 0) {
      q += ` and ${komkeyFilter}`;
    }

    const meterDataTypeFilter = this.meterFilterService.setMeterTypesCodeFilter(['MT_SMD']);
    if (meterDataTypeFilter.length > 0) {
      q += ` and ${meterDataTypeFilter}`;
    }

    q += ` and usageEndDate ne null`;

    return this.http.get<MeterView[]>(url + `?${q}`);
  }

  public findMeterByGuid(guid: string): Observable<MeterView | null> {
    const url = this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1?$filter=guid eq ${guid}`);

    return this.http.get<MeterView[]>(url).pipe(map((a: MeterView[]) => (a.length > 0 ? a[0] : null)));
  }

  updateNotesById(meterData: any, municipalityKey: string) {
    const payload = this.patchMapColumnNames(meterData, this.dbColumnNames);
    return this.http.patch(this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/meters/${meterData.guid}`), payload);
  }

  getMeterValidations(municipalityKey: string, contractAccountNumber: string): Observable<any> {
    return this.http.get(this.endpoint('csc', 'meterreadings', 'status'), {
      headers: new HttpHeaders({
        'X-Restriction-Municipality': municipalityKey,
        'X-Restriction-ContractAccount': contractAccountNumber,
      }),
    });
  }

  public updateMeterData(request: MeterWriting, municipalityKey: string, contractAccountNumber: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(this.endpoint(`csc`, `meterreadings/count`), request, {
      headers: new HttpHeaders({
        'X-Restriction-Municipality': municipalityKey,
        'X-Restriction-ContractAccount': contractAccountNumber,
      }),
    });
  }

  public getMetersByConnectionNumber(municipalityKey: string, connectionNumber: string[], usageEndDateFilter: string | null = null): Observable<MeterView[]> {
    const url = this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1`);

    let q = '';

    q += `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    const connObjFilter = this.meterFilterService.setBuildingFilter(connectionNumber);
    if (connObjFilter.length > 0) {
      q += ` and ${connObjFilter}`;
    }

    const isActiveOnlyFilter = this.meterFilterService.setActiveOnlyFilter(usageEndDateFilter);
    if (isActiveOnlyFilter.length > 0) {
      q += ` and ${isActiveOnlyFilter}`;
    }

    const komkeyFilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey);
    if (komkeyFilter.length > 0) {
      q += ` and ${komkeyFilter}`;
    }

    return this.http.get<MeterView[]>(url + `?${q}`);
  }

  public getCountOfMetersWithSensor(municipalityKey: string): Observable<MeterViewResponse> {
    const url = this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1`);

    let q = '';

    q += `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    const komkeyfilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey);
    if (komkeyfilter.length > 0) {
      q += ` and ${komkeyfilter}`;
    }

    const isActiveOnlyFilter = this.meterFilterService.setActiveOnlyFilter();
    if (isActiveOnlyFilter.length > 0) {
      q += ` and ${isActiveOnlyFilter}`;
    }

    const sensorFilter = this.meterFilterService.setSensorFilter();
    q += ` and ${sensorFilter}`;

    q += `&$top=1`; // because only count is needed
    q += `&$count=true`;

    return this.http.get<MeterViewResponse>(url + `?${q}`);
  }

  updateMeter(data: any) {
    const payload = this.patchMapColumnNames(data, this.dbColumnNames);
    return this.http.patch(this.endpoint(`socrates/meters/api/municipalities/${data.municipalityKey}/meters/${data.guid}`), payload);
  }

  public simulateMetersError<T>(): Observable<T> {
    const uri = this.endpoint(`socrates/meters/api/municipalities/0123456/meters/offline`);
    return this.http.get<T>(uri);
  }

  public getMunicipalityMeter<T>(payload: GetMunicipalityMeter): Observable<T> {
    let params = `$filter=1 eq 1 and isFlatRate eq false`;

    const url = this.endpoint(`socrates/meters/api/municipalities/${payload.municipalityKey}/meters`);

    if (!isNil(payload.connectionObjectNumber)) {
      const connObjFilter = this.meterFilterService.setBuildingFilter(payload.connectionObjectNumber);
      if (connObjFilter.length > 0) {
        params += ` and ${connObjFilter}`;
      }
    }

    if (!!payload.meterNumbers.length) {
      const meterNoFilter = this.meterFilterService.setMeterNumbersFilter(payload.meterNumbers);
      if (meterNoFilter.length > 0) {
        params += ` and ${meterNoFilter}`;
      }
    }

    params += this.meterFilterService.getMeterReadingsAll(payload.usageEndDateFilter, payload.meterReadingType);

    return this.http.get<T>(url + `?${params}`);
  }

  public getMunicipalityMeterNumbers(payload: GetMunicipalityMeterNumbers): Observable<MunicipalityMeterNumber[]> {
    let params = `$filter=1 eq 1 and isFlatRate eq false`;

    const url = this.endpoint(`socrates/meters/api/municipalities/${payload.municipalityKey}/meters`);

    const komkeyfilter = this.meterFilterService.setKomKeyFilters(payload.isForMeterReading, payload.municipalityKey);
    if (komkeyfilter.length > 0 && payload.getDistinct) {
      params += ` and ${komkeyfilter}`;
    }

    const activeOnlyFilter = this.meterFilterService.setActiveOnlyFilter(payload.usageEndDateFilter);
    if (activeOnlyFilter.length > 0) {
      params += ` and ${activeOnlyFilter}`;
    }

    params += `&$select=MeterNumber`;

    return this.http.get<MunicipalityMeterNumber[]>(url + `?${params}`);
  }

  private transformFilterOptions(options: FilterOptions): FilterOptions {
    for (const key of Object.keys(options) as Array<keyof FilterOptions>) {
      if (isNil(options[key])) {
        options[key] = '' as FilterOptionTypes;
      }
    }
    return options;
  }

  private setMsbCategory(msbCategory: string, name: string): string {
    const otherOperators = this.translate.instant('module.meterData.services.otherOperators');
    if (msbCategory !== '') {
      return msbCategory;
    } else if (name === otherOperators) {
      return MsbCategories.GMSB;
    }
    return '';
  }
}

export interface MeterDistributor extends Partial<MeterView> {
  profile1External: string;
  registerType: string;
  energyTypeName: string;
  gridLevelDescription: string;
  municipalityDiscount: string;
  count: number;
  amountYear: number;
  calendarYear: string;
  objectName: string;
  meterOperator: string;
}

interface GetMunicipalityMeter {
  municipalityKey: string;
  isForMeterReading: boolean;
  meterReadingType: MeterReadingType;
  usageEndDateFilter: string;
  meterNumbers: string[];
  connectionObjectNumber?: string[];
  getMeterRegister?: boolean;
}

interface GetMunicipalityMeterNumbers {
  municipalityKey: string;
  isForMeterReading: boolean;
  usageEndDateFilter: string;
  getDistinct: boolean;
}
